import React, { useState, useRef, useEffect } from "react";
import { updateWork } from "../../../../api/hub/work";
import { titleCase, fmtDisplayDate, fmtNum, safeParseJson } from "../utils";
import { statusDisplayName, getServiceColor } from "../constants";
import { getMyClients } from "../../../../api/hub";
import { useToast } from "../../../../contexts/ToastContext";

// ─── TaskHeader ─────────────────────────────────────────────────────────────────
// Title, client (clickable → slide-out), status, owner, due vs forecast.
// fn_forecast_task returns days_open, median_days, p90_days, observations,
// past_p90, times_typical, and a basis sentence. ai_forecast_at is NOT a due
// date — they look different, and low confidence looks different again.
// Status pill colour — degrades to neutral grey for unknown statuses.
const statusPillClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "needs_assistance" || s === "blocked" || s === "stalled")
    return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400";
  if (s === "done" || s === "completed" || s === "closed" || s === "resolved")
    return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400";
  if (s.includes("overdue"))
    return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
  return "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400";
};

// Priority pill colour — translucent tint + accent text + subtle border, per
// level, so the pill is scannable but doesn't outshine the task title (Item 5).
// Mirrors the priorityPillClass helper in InboxRow.js so the row and the slide-out
// agree (urgent/high=red, medium/normal=amber, low=neutral). Degrades to neutral
// grey for unknown values. This replaces the earlier inline backend-hex
// background, which had no dark-mode text override and rendered near-black text
// on the dark slide-out when the hex was absent (Item 1). The previous solid-ish
// tint (bg-red-100 …) was still louder than the title on the dark slide-out;
// bg-{color}-500/10 is more translucent and the border keeps it defined without
// a heavy fill. priorityValue may be a raw label like "high" or "urgent";
// callers normalise the JSON via safeParseJson.
const priorityPillClass = (priority) => {
  const p = String(priority || "").toLowerCase();
  if (p === "urgent" || p === "p0" || p === "high")
    return "bg-red-500/10 text-red-700 dark:bg-red-500/10 dark:text-red-400 border border-red-500/30";
  if (p === "medium" || p === "p1" || p === "normal")
    return "bg-amber-500/10 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400 border border-amber-500/30";
  if (p === "low" || p === "p2")
    return "bg-gray-500/10 text-gray-600 dark:bg-gray-500/10 dark:text-gray-400 border border-gray-500/30";
  return "bg-gray-500/10 text-gray-600 dark:bg-gray-500/10 dark:text-gray-400 border border-gray-500/30";
};

// ─── Service code → display label (Item 4) ───────────────────────────────────
// task.service normally carries a human label (e.g. "Website Design"), but a
// dropdown change writes the raw service CODE into it — onServiceChange sends
// opt.code, and handleServiceChange patches task.service with that code — so the
// pill would render "website_design" instead of "Website Design". This map is
// keyed on the same codes as SERVICE_COLORS in constants.js; getServiceLabel
// looks the value up and falls back to the value as-is (already a label for
// legacy/unchanged tasks) so a new service never shows its snake_case key.
const SERVICE_LABELS = {
  website_design: "Website Design",
  google_ads: "Google Ads",
  seo: "SEO",
  social_media: "Social Media",
  email_marketing: "Email Marketing",
  content_writing: "Content Writing",
  paid_ads: "Paid Ads",
  video_production: "Video Production",
};
const getServiceLabel = (value, fallbackCode = "") =>
  (value != null && SERVICE_LABELS[value]) || value || (fallbackCode ? titleCase(String(fallbackCode)) : "");

// ─── Description link parsing ─────────────────────────────────────────────────
// URLs in the description (e.g. a Google Docs link) should render as clickable
// anchors instead of plain text. parseDescriptionSegments splits a string into
// an ordered array of segments — text segments carry the surrounding prose,
// link segments carry a bare URL. The view-mode render below maps these into
// spans / <a> elements. A description with no URLs collapses to a single text
// segment, so the no-URL path is identical to a plain string render.
const URL_RE = /https?:\/\/[^\s]+/g;
const parseDescriptionSegments = (text) => {
  if (!text) return [{ type: "text", content: "" }];
  const segments = [];
  let lastIndex = 0;
  let match;
  // Reset the global regex state in case it was reused.
  URL_RE.lastIndex = 0;
  while ((match = URL_RE.exec(text)) !== null) {
    const url = match[0];
    const start = match.index;
    if (start > lastIndex) {
      segments.push({ type: "text", content: text.slice(lastIndex, start) });
    }
    segments.push({ type: "link", url });
    lastIndex = start + url.length;
  }
  if (lastIndex < text.length) {
    segments.push({ type: "text", content: text.slice(lastIndex) });
  }
  // No matches at all → single text segment (preserves the original string).
  if (segments.length === 0) segments.push({ type: "text", content: text });
  return segments;
};

// ─── External-link icon (inline SVG; lucide-react is not a dependency) ──────
// Paths mirror lucide-react's ExternalLink glyph so the visual matches the
// tracker spec without adding a new dependency.
const ExternalLinkIcon = ({ className = "" }) => (
  <svg
    className={className}
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M15 3h6v6" />
    <path d="M10 14 21 3" />
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
  </svg>
);

// Render an array of description segments. Text segments render as spans (the
// parent <p> keeps whitespace-pre-wrap so newlines are preserved); link segments
// render as an inline-flex anchor with the external-link icon. A segment key is
// derived from its index — segment arrays are short-lived per render, so this
// is stable enough for React reconciliation here.
const renderDescriptionSegments = (segments) =>
  segments.map((seg, i) =>
    seg.type === "link" ? (
      <a
        key={`link-${i}`}
        href={seg.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
        onClick={(e) => e.stopPropagation()}
      >
        {seg.url}
        <ExternalLinkIcon className="flex-shrink-0" />
      </a>
    ) : (
      <span key={`text-${i}`}>{seg.content}</span>
    )
  );

// ─── Summary cleaning ──────────────────────────────────────────────────────────
// fn_work_detail's AI `summary` embeds an analytics parenthetical mid-sentence,
// e.g. "The task was updated today (days_since_update=0) and has a recent
// comment…". Strip just the "(days_since_update=N)" fragment (and its leading
// space, so we don't leave a double space) and keep the rest of the note — the
// surrounding prose is meaningful and should read like a human-written line.
const cleanSummary = (raw) => {
  if (!raw) return "";
  let text = String(raw);
  text = text.replace(/\s*\(days_since_update[=:]\s*\d+\)/gi, "");
  text = text.replace(/\n{3,}/g, "\n\n");
  return text.trim();
};

// ─── Priority pill contrast ───────────────────────────────────────────────────
// ClickUp sends priority as a stringified JSON object carrying a `color` hex
// (e.g. "#f8ae00"). Pick readable text against that background: compute the
// sRGB relative luminance and use a 0.5 threshold — light backgrounds get dark
// text, dark backgrounds get white text. Unparseable colours fall back to
// "light" (dark text); a background is only ever applied from a valid hex.
const hexIsLight = (hex) => {
  if (!hex || typeof hex !== "string") return true;
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return true;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const lum = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return lum > 0.5;
};

// ─── Repeat cadence vocabulary (work-page-fixes Item 1) ──────────────────────
// Mirrors CADENCE_OPTIONS / WEEKDAY_OPTIONS / DAY_OF_MONTH_CADENCES in
// WorkPanel.js so the slider's inline repeat editor presents the same schedule
// options as the Repeating tab. Hoisted to module scope so the arrays are not
// reallocated on every render.
const CADENCE_OPTIONS = [
  { value: "daily", label: "Every day" },
  { value: "weekly", label: "Every week" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Every month" },
  { value: "quarterly", label: "Every 3 months" },
  { value: "yearly", label: "Every year" },
];
const WEEKDAY_OPTIONS = [
  { value: 0, label: "Sunday" }, { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" }, { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" }, { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];
const DAY_OF_MONTH_CADENCES = new Set(["monthly", "quarterly"]);

const TaskHeader = ({ task, taskId, forecast, onClientClick, onTaskUpdated, onTitleChange, onDueDateChange, onAssigneeClick, onDescriptionChange, showError, showSuccess, onPriorityChange, priorityOptions, onServiceChange, serviceOptions, positions, onPositionClick, workTypeOptions, onWorkTypeChange, onClientChange, repeat }) => {
  // ─── Inline editing (tracker: clickable title & due date) ────────────────────
  // Separate from the full-form `editing` mode below. Click the title or the due
  // date to edit just that field inline; Enter/blur saves via onTitleChange /
  // onDueDateChange (WorkPage calls fn_work_update), Escape cancels.
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(task?.title || "");
  const [editingDue, setEditingDue] = useState(false);
  const [dueValue, setDueValue] = useState(task?.due || "");
  // Refs guard the Escape→blur race: Escape sets the flag then blurs; the blur
  // handler reads + clears the flag so it cancels instead of saving.
  const cancelTitleRef = useRef(false);
  const cancelDueRef = useRef(false);
  // Ref to the inline date input so the activating click can open the native
  // date picker. The input isn't mounted at click time, so the picker-open
  // call is made in a useEffect that watches editingDue — useEffect runs
  // after React commits the re-render, so the ref is guaranteed attached.
  // requestAnimationFrame fired before the commit, leaving the ref null on
  // the first click. See work-page-fixes §C.
  const dueInputRef = useRef(null);

  // Open the native date picker once editingDue flips to true. useEffect runs
  // after React has committed the DOM update, so the date input is mounted and
  // dueInputRef.current is set before this fires — unlike requestAnimationFrame,
  // which can run before the commit and leave the ref null on the first click.
  // Only act on the true transition; when editingDue goes back to false the
  // input is being torn down and there is nothing to open. Optional chaining
  // guards browsers without showPicker support (they fall back to autoFocus).
  useEffect(() => {
    if (!editingDue) return;
    dueInputRef.current?.showPicker?.();
  }, [editingDue]);

  // ─── Description expand/collapse (view mode only) ───────────────────────────
  // Long descriptions are collapsed to a preview; "See more" reveals the full
  // cleaned text. Edit mode still shows the raw description in the form.
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  // Stalled-reason "See more/See less" toggle (Item 2 — auto-truncate like the
  // Description preview). Only the long amber stalled_reason is collapsed; the
  // Situation heading and gray summary stay visible.
  const [stalledExpanded, setStalledExpanded] = useState(false);

  // ─── Inline description editing (click-to-edit, same pattern as title) ───────
  // Click the description <p> to swap to a textarea; Enter (no Shift) blurs to
  // save, Escape cancels via the cancel-ref flag (guards the Escape→blur race).
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState(task?.description || "");
  const cancelDescRef = useRef(false);
  // task.description loads asynchronously — useState only seeds the initial
  // value, so resync whenever it changes and we're not actively editing.
  useEffect(() => {
    if (!editingDescription) setDescriptionValue(task?.description || "");
  }, [task?.description, editingDescription]);

  // ─── Inline-editable priority & service dropdowns ────────────────────────────
  // Clicking the priority/service pill opens a small positioned dropdown. On
  // selection we call onPriorityChange/onServiceChange (WorkPage calls
  // fn_work_update) and close. A document mousedown listener closes whichever
  // dropdown is open when the click lands outside its pill wrapper.
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [showWorkTypeDropdown, setShowWorkTypeDropdown] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const priorityPillRef = useRef(null);
  const servicePillRef = useRef(null);
  const workTypePillRef = useRef(null);
  const clientPickerRef = useRef(null);
  // Client-picker roster (Item 2) — fetched from fn_get_my_clients on first
  // open of the client dropdown and cached in state so a re-open doesn't
  // refetch. The full reusable client-search component (pagination, avatars)
  // is a follow-up; this is the minimal name-filterable list.
  const [clientList, setClientList] = useState([]);
  const [clientListLoading, setClientListLoading] = useState(false);
  const [clientListError, setClientListError] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  useEffect(() => {
    if (!showPriorityDropdown && !showServiceDropdown && !showWorkTypeDropdown && !showClientDropdown) return;
    const onDown = (e) => {
      if (showPriorityDropdown && priorityPillRef.current && !priorityPillRef.current.contains(e.target)) setShowPriorityDropdown(false);
      if (showServiceDropdown && servicePillRef.current && !servicePillRef.current.contains(e.target)) setShowServiceDropdown(false);
      if (showWorkTypeDropdown && workTypePillRef.current && !workTypePillRef.current.contains(e.target)) setShowWorkTypeDropdown(false);
      if (showClientDropdown && clientPickerRef.current && !clientPickerRef.current.contains(e.target)) setShowClientDropdown(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [showPriorityDropdown, showServiceDropdown, showWorkTypeDropdown, showClientDropdown]);

  // Toast context (Item 1) — the share button copies a task link and fires a
  // bottom-center toast (ToastContext), not the NotificationContext modal that
  // `showSuccess` renders. A toast is the right feedback for a one-click copy.
  const { toastSuccess, toastError } = useToast();

  // Fetch the client roster once, the first time the client picker opens.
  // fn_get_my_clients returns the user's visible clients (small set). A thrown
  // error is Postgres-level, so it is never shown verbatim — the popover
  // surfaces a friendly "Failed to load clients" and logs the detail.
  useEffect(() => {
    if (!showClientDropdown || clientList.length > 0) return;
    let cancelled = false;
    setClientListLoading(true);
    setClientListError(null);
    getMyClients()
      .then((resp) => { if (!cancelled) setClientList(resp?.data ?? []); })
      .catch((e) => { if (!cancelled) { console.error("Client list fetch failed:", e); setClientListError("Failed to load clients"); } })
      .finally(() => { if (!cancelled) setClientListLoading(false); });
    return () => { cancelled = true; };
  }, [showClientDropdown, clientList.length]);

  if (!task) return null;

  const hasDue = !!task.due;

  // ─── Overdue detection ────────────────────────────────────────────────────────
  // The backend sets `overdue` on the task (see fn_work_list), but also treat a
  // due date that is simply in the past as overdue. `task.due` is a YYYY-MM-DD
  // string (fmtDisplayDate parses it as date-only), so a lexicographic compare
  // against today's YYYY-MM-DD is safe and avoids timezone drift.
  const todayStr = new Date().toISOString().slice(0, 10);
  const isOverdue = !!task.overdue || (!!task.due && String(task.due).slice(0, 10) < todayStr);

  // ─── Days-in-status badge ────────────────────────────────────────────────────
  // "How long has this been sitting" — days in the current status. Hidden when
  // null or zero. Colour escalates with duration: neutral up to 30, amber over
  // 30, red over 90. Returned as { d, cls } or null so the render can short-circuit.
  const daysInStatus = (() => {
    const d = Number(task.days_in_status);
    if (!Number.isFinite(d) || d <= 0) return null;
    const cls =
      d > 90 ? "text-red-400 bg-red-400/10"
      : d > 30 ? "text-amber-400 bg-amber-400/10"
      : "text-neutral-400 bg-neutral-800";
    return { d, cls };
  })();

  // ─── Normalize JSON-encoded fields ───────────────────────────────────────────
  // ClickUp migration left some fields as raw JSON strings (e.g.
  // `{"id":"2","color":"#f8ae00","priority":"high","orderindex":"2"}`). Parse them
  // and extract the meaningful value so we never render raw JSON in the header.
  const priorityRaw = safeParseJson(task.priority);
  const priorityValue =
    priorityRaw && typeof priorityRaw === "object"
      ? priorityRaw.priority || priorityRaw.name || ""
      : priorityRaw;
  // A priority is "present" only with a real label (not null / "none"). When
  // absent the whole pill slot is hidden — no empty "Set priority" badge.
  const hasPriority = !!(priorityValue && priorityValue !== "none");
  const serviceRaw = safeParseJson(task.service);
  const serviceValue =
    serviceRaw && typeof serviceRaw === "object"
      ? serviceRaw.name || serviceRaw.service || ""
      : serviceRaw;
  // Display label for the service pill (Item 4). serviceValue is a human label
  // for unchanged tasks but becomes the raw service code after a dropdown
  // change (handleServiceChange writes the code into task.service). Look it up
  // in SERVICE_LABELS; if it isn't a known code it's already a label, so fall
  // back to it. The final titleCase(service_code) guard covers an empty value
  // paired with a known code.
  const serviceLabel =
    getServiceLabel(serviceValue, task.service_code);

  // ─── Work type label (display pill; editable via the dropdown below) ──────────
  // task.work_type is a code (e.g. "delivery", "editorial", "internal"). Match it
  // against workTypeOptions for a human label; fall back to a title-cased raw
  // code. The pill is now clickable (Item 2): it opens a dropdown of work types
  // whose selected value is sent to onWorkTypeChange(taskId, code).
  const workTypeLabel = (() => {
    const wt = task.work_type;
    if (!wt) return "";
    const match = (workTypeOptions || []).find((o) => o.value === wt || o.code === wt);
    return match?.label || titleCase(String(wt).replace(/_/g, " "));
  })();

  // Client-picker filter (Item 2) — case-insensitive name match against the
  // roster fetched on first open. Empty search shows the whole roster.
  const filteredClients = (() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return clientList;
    return clientList.filter((c) => String(c.name || "").toLowerCase().includes(q));
  })();

  // ─── Tags (may be a JSON array or a comma-separated string) ────────────────
  // fn_work_detail returns tags as either a JSON-encoded array or a plain
  // comma-separated string. Normalise to an array of trimmed, non-empty tags
  // so the view-mode render below can iterate without re-parsing.
  const tagsRaw = safeParseJson(task.tags);
  const parsedTags = Array.isArray(tagsRaw)
    ? tagsRaw.map((t) => String(t ?? "").trim()).filter(Boolean)
    : typeof tagsRaw === "string" && tagsRaw.trim()
      ? tagsRaw.split(",").map((t) => t.trim()).filter(Boolean)
      : [];

  // ─── Description cleaning & expand/collapse ────────────────────────────────
  // ClickUp descriptions carry boilerplate (name–action headers, "Deadline:",
  // "Description:" prefixes, "Full Transcript:" URLs). Strip those so the
  // view-mode preview shows the actual content. The raw text remains editable
  // in edit mode.
  const cleanDescription = (raw) => {
    if (!raw) return "";
    let text = String(raw);
    text = text.replace(/^[^\n]+–[^\n]+\n\n/, "");   // remove name–action header line
    text = text.replace(/Deadline:[^\n]+\n?/g, "");   // remove Deadline: lines
    text = text.replace(/^Description:\s*/gm, "");   // remove "Description:" prefix
    text = text.replace(/Full Transcript:[^\n]+\n?/g, ""); // remove Full Transcript: lines
    text = text.replace(/\n{3,}/g, "\n\n");          // collapse triple+ newlines
    return text.trim();
  };
  const cleanDesc = task.description ? cleanDescription(task.description) : "";
  const DESC_PREVIEW_LIMIT = 150;
  const isLongDesc = cleanDesc.length > DESC_PREVIEW_LIMIT;
  const descPreview = (() => {
    if (!isLongDesc) return cleanDesc;
    // First ~150 chars, but cut at the end of the nearest paragraph if shorter.
    const paragraphs = cleanDesc.split(/\n\n/);
    if (paragraphs.length > 1 && paragraphs[0].length <= DESC_PREVIEW_LIMIT) {
      return paragraphs.slice(0, 2).join("\n\n");
    }
    return cleanDesc.slice(0, DESC_PREVIEW_LIMIT);
  })();

  // ─── Summary cleaning ──────────────────────────────────────────────────────────
  // fn_work_detail's `summary` is AI-generated and sometimes carries metadata
  // lines (e.g. "days_since_update=0", "Task was updated…", "3 days since…").
  // Strip them before rendering the Situation block; if nothing remains the
  // section is hidden (see the Situation guard below).
  const cleanedSummary = cleanSummary(task.summary || "");

  // ─── Stalled reason truncation (auto-truncate like Description) ────────────
  // The stalled_reason is the amber "why is this stuck" text inside the Situation
  // card. Long reasons are previewed (~150 chars) with a "See more/See less"
  // toggle, mirroring the Description preview. Short reasons render in full.
  const stalledReason = task.stalled_reason ? String(task.stalled_reason) : "";
  const STALLED_PREVIEW_LIMIT = 150;
  const isLongStalled = stalledReason.length > STALLED_PREVIEW_LIMIT;
  const stalledPreview = (() => {
    if (!isLongStalled) return stalledReason;
    const paragraphs = stalledReason.split(/\n\n/);
    if (paragraphs.length > 1 && paragraphs[0].length <= STALLED_PREVIEW_LIMIT) {
      return paragraphs.slice(0, 2).join("\n\n");
    }
    return stalledReason.slice(0, STALLED_PREVIEW_LIMIT);
  })();

  // ─── Inline-edit commit handlers (title / due) ───────────────────────────────
  // Single commit path = the input's blur. Enter calls blur (save); Escape sets
  // a cancel flag then calls blur (cancel). This avoids a double-save on the
  // Enter→blur sequence.
  const commitTitle = () => {
    const cancelled = cancelTitleRef.current;
    cancelTitleRef.current = false;
    setEditingTitle(false);
    if (cancelled) { setTitleValue(task.title || ""); return; }
    const trimmed = (titleValue || "").trim();
    if (trimmed !== (task.title || "")) onTitleChange?.(taskId, trimmed);
  };
  const commitDue = () => {
    const cancelled = cancelDueRef.current;
    cancelDueRef.current = false;
    setEditingDue(false);
    if (cancelled) { setDueValue(task.due || ""); return; }
    if ((dueValue || "") !== (task.due || "")) onDueDateChange?.(taskId, dueValue || null);
  };
  const commitDescription = () => {
    const cancelled = cancelDescRef.current;
    cancelDescRef.current = false;
    setEditingDescription(false);
    if (cancelled) { setDescriptionValue(task.description || ""); return; }
    const trimmed = (descriptionValue || "").trim();
    if (trimmed !== (task.description || "").trim()) onDescriptionChange?.(taskId, trimmed);
  };

  // ─── Share / copy link (Item 1 — toast, no dropdown) ───────────────────────
  // The share button is a single action now (no dropdown): it copies the task
  // URL to the clipboard and fires a ToastContext toast. "Send message" was
  // removed (Item 1). A toast — not the NotificationContext modal that
  // showSuccess renders — is the right feedback for a one-click copy.
  const handleCopyLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}?task=${taskId}`;
    try {
      await navigator.clipboard.writeText(url);
      toastSuccess("Link copied");
    } catch (e) {
      toastError("Couldn't copy the link");
    }
  };

  return (
    <div className="px-6 pt-4 pb-2">
      {/* ─── Title (largest, boldest) + edit ────────────────────────── */}
      <div className="flex items-start justify-between gap-3">
        {editingTitle ? (
          <input
            type="text"
            value={titleValue}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setTitleValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); e.target.blur(); }
              else if (e.key === "Escape") { cancelTitleRef.current = true; setTitleValue(task.title || ""); e.target.blur(); }
            }}
            onBlur={commitTitle}
            className="flex-1 text-lg font-bold border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          />
        ) : (
          <div
            className="group cursor-pointer rounded px-2 py-1 -mx-2 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors flex items-start justify-between gap-2 flex-1 min-w-0"
            onClick={() => { setTitleValue(task.title || ""); setEditingTitle(true); }}
          >
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1 leading-snug flex-1">
              {task.title || "Untitled task"}
            </h2>
            <svg className="w-3 h-3 mt-1.5 text-neutral-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
            </svg>
          </div>
        )}
        {/* ─── Share + View in ClickUp (stacked vertically, top-right of title row) */}
        <div className="flex flex-col items-end gap-0.5">
          {/* ─── Share / copy link (Item 1 — direct action, no dropdown) ─ */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleCopyLink(); }}
            title="Copy task link"
            aria-label="Copy task link"
            className="inline-flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 mt-1 p-1 rounded hover:bg-gray-100 dark:hover:bg-white/5 transition-colors flex-shrink-0"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
              <polyline points="16 6 12 2 8 6" />
              <line x1="12" y1="2" x2="12" y2="15" />
            </svg>
          </button>
          {/* ─── View in ClickUp (below share icon — Item 3) ──────────── */}
          {task.source_url && (
            <a
              href={task.source_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 mt-1 flex-shrink-0"
            >
              View in ClickUp
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </a>
          )}
        </div>
      </div>

      {/* ─── Client (directly below title — Item 3: connected pair) ──── */}
      {/* Renders task.client as a clickable button (→ ClientSlideOut) or, when
          null, "Internal" in muted italic. The pencil icon opens a client
          picker popover (Item 2): a name-filterable list from fn_get_my_clients;
          selecting one calls onClientChange(taskId, id) (WorkPage calls
          fn_work_update with p_client_id). Both interactions stopPropagation so
          they don't bubble to parent click handlers. The popover anchors to
          clientPickerRef. */}
      <div ref={clientPickerRef} className="group relative mb-1 ml-0">
        <div className="flex items-center gap-1">
          {task.client ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onClientClick?.(task.client_id); }}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-blue-500 cursor-pointer"
            >
              {task.client}
            </button>
          ) : (
            <span className="text-neutral-500 text-sm italic">
              Internal
            </span>
          )}
          {/* Edit affordance (Item 1) — pencil opens the client selection
              dropdown. Clicking the client name itself still opens the
              ClientSlideOut via onClientClick; the pencil is a separate
              trigger so navigation and editing both stay available. Hidden
              until hover (group-hover) to keep the header clean. Mirrors the
              work-type pill toggle (setShowWorkTypeDropdown) and the
              description pencil SVG below. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowClientDropdown(!showClientDropdown); }}
            title="Change client"
            aria-label="Change client"
            className="inline-flex items-center justify-center cursor-pointer text-gray-400 dark:text-gray-500 hover:text-blue-500"
          >
            <svg className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
            </svg>
          </button>
        </div>
        {showClientDropdown && (
          <div className="absolute z-50 mt-1 left-0 w-64 max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1" onClick={(e) => e.stopPropagation()}>
            <div className="px-2 pb-1">
              <input
                type="text"
                autoFocus
                value={clientSearch}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setClientSearch(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Search clients..."
                className="w-full text-xs border border-gray-200 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            {/* Internal (no client) — clears the client (onClientChange(taskId,
                null) → fn_work_update p_client_id null). Mirrors the
                CreateTaskModal reset option ("Internal (no client)"); highlighted
                + checked when no client is set. Sits above the divider so it's
                always reachable, even while the client list loads or a search
                filters the results. */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onClientChange?.(taskId, null); setShowClientDropdown(false); setClientSearch(""); }}
              className={`flex items-center justify-between w-full text-left text-xs px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 ${!task.client_id ? "text-blue-600 dark:text-blue-400 font-medium" : "text-gray-700 dark:text-gray-300"}`}
            >
              <span>Internal (no client)</span>
              {!task.client_id && (
                <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
            <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
            {clientListLoading ? (
              <div className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500">Loading...</div>
            ) : clientListError ? (
              <div className="px-3 py-2 text-xs text-red-500 dark:text-red-400">{clientListError}</div>
            ) : filteredClients.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500">No clients found</div>
            ) : (
              filteredClients.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onClientChange?.(taskId, c.id); setShowClientDropdown(false); setClientSearch(""); }}
                  className={`w-full text-left text-xs px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 ${c.id === task.client_id ? "text-blue-600 dark:text-blue-400 font-medium" : "text-gray-700 dark:text-gray-300"}`}
                >
                  {c.name}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* ─── Pills row: work type + priority + service ──────────────── */}
      {/* gap-3 (not gap-2): each pill uses -mx-2 which bleeds its hover bg 8px
          outward. gap-2 (8px) let one pill's hover bg reach the next pill's
          text. gap-3 (12px) leaves ~4px clearance so hover backgrounds stay
          within their own pill (Item 1). */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        {/* Work type pill — click to edit inline (opens a dropdown of work types
            from ops_work_types via workTypeOptions). On select calls
            onWorkTypeChange(taskId, code) (WorkPage calls fn_work_update with
            p_work_type). Hidden when task.work_type is null. stopPropagation on
            the pill and dropdown so the click doesn't bubble. */}
        {workTypeLabel && (
        <div
          className="group cursor-pointer rounded px-2 py-1 -mx-2 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors inline-flex items-center"
          onClick={(e) => { e.stopPropagation(); setShowWorkTypeDropdown(!showWorkTypeDropdown); }}
        >
          <div ref={workTypePillRef} className="relative flex items-center">
            <span className="bg-neutral-700 text-neutral-300 text-xs px-2 py-0.5 rounded">
              {workTypeLabel}
            </span>
            {showWorkTypeDropdown && (
              <div className="absolute z-50 mt-1 left-0 min-w-[160px] max-h-56 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1" onClick={(e) => e.stopPropagation()}>
                {(workTypeOptions || []).map((opt) => {
                  const wtValue = opt.value || opt.code;
                  return (
                    <button
                      key={wtValue}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onWorkTypeChange?.(taskId, wtValue); setShowWorkTypeDropdown(false); }}
                      className={`w-full text-left text-xs px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 ${wtValue === task.work_type ? "text-blue-600 dark:text-blue-400 font-medium" : "text-gray-700 dark:text-gray-300"}`}
                    >
                      {opt.label || opt.name || wtValue}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        )}
        {/* Priority pill — click to edit inline (opens a dropdown).
            Always shown; "Set priority" placeholder when no priority set. */}
        <div
          className="group cursor-pointer rounded px-2 py-1 -mx-2 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors inline-flex items-center"
          onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
        >
          <div ref={priorityPillRef} className="relative flex items-center">
            {hasPriority ? (
              <span
                className={`text-xs font-medium px-1.5 py-0.5 rounded ${priorityPillClass(priorityValue)}`}
              >
                {titleCase(priorityValue)}
              </span>
            ) : (
              <span className="text-sm text-gray-400 dark:text-gray-500 italic cursor-pointer">
                Priority
              </span>
            )}
            {showPriorityDropdown && (
              <div className="absolute z-50 mt-1 left-0 min-w-[140px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1" onClick={(e) => e.stopPropagation()}>
                {(priorityOptions || []).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onPriorityChange?.(taskId, opt.value); setShowPriorityDropdown(false); }}
                    className="w-full text-left text-xs px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                  >
                    {opt.label}
                  </button>
                ))}
                {/* "none" clears the priority — not in PRIORITY_OPTIONS, appended here */}
                <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onPriorityChange?.(taskId, "none"); setShowPriorityDropdown(false); }}
                  className="w-full text-left text-xs px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                >
                  No priority
                </button>
              </div>
            )}
          </div>
        </div>
        {/* Service pill — click to edit inline (opens a dropdown).
            Always shown; "Set service" placeholder when no service set. The
            pill background comes from SERVICE_COLORS via getServiceColor(code);
            text colour is luminance-derived (hexIsLight) for readability. */}
        <div
          className="group cursor-pointer rounded px-2 py-1 -mx-2 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors inline-flex items-center"
          onClick={() => setShowServiceDropdown(!showServiceDropdown)}
        >
          <div ref={servicePillRef} className="relative flex items-center">
            {serviceValue && task.service_code ? (
              <span
                className={`text-xs px-2 py-0.5 rounded ${
                  hexIsLight(getServiceColor(task.service_code)) ? "text-neutral-900" : "text-white"
                }`}
                style={{ backgroundColor: getServiceColor(task.service_code) }}
              >
                {serviceLabel}
              </span>
            ) : (
              <span className="text-sm text-gray-400 dark:text-gray-500 italic cursor-pointer">
                Service
              </span>
            )}
            {showServiceDropdown && (
              <div className="absolute z-50 mt-1 left-0 min-w-[160px] max-h-56 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1" onClick={(e) => e.stopPropagation()}>
                {(serviceOptions || []).map((opt) => (
                  <button
                    key={opt.code}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onServiceChange?.(taskId, opt.code); setShowServiceDropdown(false); }}
                    className="w-full text-left text-xs px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Owner (left) / Due (right) row ─────────────────────────── */}
      {/* Mobile: stack vertically (flex-col). Desktop (md+): side-by-side row. */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1 md:gap-3 mb-3 text-sm">
        {/* Left group: owner + days-in-status badge ("how long has this been sitting") */}
        <div className="flex items-center gap-2 min-w-0 w-full md:w-auto">
          {/* Owner — clickable → AssignmentPopover */}
          <div
            className="group cursor-pointer rounded px-2 py-1 -mx-2 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors inline-flex items-center gap-1.5 min-w-0 flex-1 md:flex-none"
            onClick={() => onAssigneeClick?.()}
          >
            <span className="text-neutral-500 text-xs whitespace-nowrap">Assignee</span>
            <span className="text-left text-gray-700 dark:text-gray-300 truncate whitespace-nowrap min-w-0">
              {task.owner || "Unassigned"}
            </span>
            <svg className="w-3 h-3 text-neutral-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
            </svg>
          </div>
          {/* Days in current status — hidden when null/zero; colour escalates with age */}
          {daysInStatus && (
            <span
              className={`text-xs px-1.5 py-0.5 rounded-full ${daysInStatus.cls}`}
              title={`${daysInStatus.d} days in current status`}
            >
              {daysInStatus.d}d
            </span>
          )}
          {/* Repeating badge (work-page-fixes Item 1) — shown when the task is
              recurring. Same loop icon + muted-gray pill as the list row's
              recurring badge (InboxRow.js) so the slide-out and the row agree.
              Sits in the Owner/Due row beside the days-in-status badge. */}
          {task.recurring === true && (
            <span
              className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
              title="This task recurs — it will come back"
            >
              <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="17 1 21 5 17 9" />
                <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                <polyline points="7 23 3 19 7 15" />
                <path d="M21 13v2a4 4 0 0 1-4 4H3" />
              </svg>
              Repeats
            </span>
          )}
        </div>

        {/* Due — click to edit inline */}
        {editingDue ? (
          <input
            type="date"
            ref={dueInputRef}
            value={dueValue || ""}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setDueValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); e.target.blur(); }
              else if (e.key === "Escape") { cancelDueRef.current = true; setDueValue(task.due || ""); e.target.blur(); }
            }}
            onBlur={commitDue}
            className="text-sm border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300"
          />
        ) : (
          <div
            className="group cursor-pointer rounded px-2 py-1 -mx-2 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors inline-flex items-center gap-1.5 min-w-0 w-full md:w-auto"
            onClick={() => {
              setDueValue(task.due || "");
              setEditingDue(true);
              // Opening the native picker is handled in a useEffect that watches
              // editingDue (below the state declarations): useEffect runs after
              // React commits the re-render, so the input is in the DOM and the
              // ref is attached before showPicker is called. Deferring with
              // requestAnimationFrame here fired before the commit, leaving the
              // ref null on the first click.
            }}
          >
            <span className="text-neutral-500 text-xs">Due</span>
            <span className={`text-gray-700 dark:text-gray-300 truncate whitespace-nowrap min-w-0 ${isOverdue ? "text-red-600 dark:text-red-400 font-medium" : ""}`}>
              {hasDue ? fmtDisplayDate(task.due) : "No due date"}
            </span>
            <svg className="w-3 h-3 text-neutral-500 dark:text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
            </svg>
          </div>
        )}
      </div>

      {/* Repeats row (work-page-fixes Item 1) — directly below the Assignee/Due
          row, above Tags/Description. "Repeats" label on the left, schedule
          label + toggle on the right. The schedule label is clickable (accent
          underline on hover) — clicking it turns the label into inline cadence
          and day dropdowns (the slider's inline-edit pattern, like the due
          date). Changes commit immediately on selection (no Set/Cancel); a
          small close button collapses the controls back to the label. */}
      {repeat && (
        <div className="flex items-center justify-between gap-3 mb-3 text-sm">
          <span className="text-neutral-500 text-xs whitespace-nowrap">Repeats</span>
          <div className="flex items-center gap-2 min-w-0">
            {repeat.open ? (
              <div className="flex items-center gap-1.5 flex-wrap justify-end">
                <select
                  value={repeat.cadence}
                  disabled={repeat.saving}
                  onChange={(e) => repeat.onCadenceChange?.(e.target.value)}
                  className="text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 px-2 py-1 focus:border-blue-500 focus:outline-none"
                >
                  {CADENCE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {repeat.cadence === "weekly" && (
                  <select
                    value={repeat.dayOfWeek}
                    disabled={repeat.saving}
                    onChange={(e) => repeat.onDayOfWeekChange?.(Number(e.target.value))}
                    className="text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 px-2 py-1 focus:border-blue-500 focus:outline-none"
                  >
                    {WEEKDAY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                )}
                {DAY_OF_MONTH_CADENCES.has(repeat.cadence) && (
                  <select
                    value={repeat.dayOfMonth}
                    disabled={repeat.saving}
                    onChange={(e) => repeat.onDayOfMonthChange?.(Number(e.target.value))}
                    className="text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 px-2 py-1 focus:border-blue-500 focus:outline-none"
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                )}
                <button
                  type="button"
                  onClick={repeat.onClose}
                  disabled={repeat.saving}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 px-1 disabled:opacity-50"
                  aria-label="Close repeat editor"
                >
                  ✕
                </button>
              </div>
            ) : repeat.toggleOn ? (
              <div
                onClick={repeat.onLabelClick}
                className="group cursor-pointer rounded px-2 py-1 -mx-2 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors inline-flex items-center gap-1.5 min-w-0"
              >
                <span className="text-gray-700 dark:text-gray-300 truncate whitespace-nowrap min-w-0">
                  {repeat.rule ? (repeat.rule.schedule_label || "Every week") : "None"}
                </span>
                <svg className="w-3 h-3 text-neutral-500 dark:text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                </svg>
              </div>
            ) : (
              <span className="text-gray-400 dark:text-gray-500 truncate whitespace-nowrap min-w-0 px-2 py-1 -mx-2">
                Not repeating
              </span>
            )}
            <button
              type="button"
              role="switch"
              aria-checked={repeat.toggleOn}
              disabled={repeat.saving}
              onClick={repeat.onToggle}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${repeat.toggleOn ? "bg-blue-500" : "bg-gray-300 dark:bg-gray-600"} ${repeat.saving ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${repeat.toggleOn ? "translate-x-4" : "translate-x-0.5"}`} />
            </button>
          </div>
        </div>
      )}


      {/* ─── Tags (read-only pills, view mode only) ──────────────────── */}
      {parsedTags.length > 0 && (
        <div className="flex items-center gap-1.5 mb-3 flex-wrap">
          {parsedTags.map((tag, i) => (
            <span
              key={`tag-${i}-${tag}`}
              className="text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* ─── Description (always shown; placeholder when empty) */}
      <div className="mb-4 border-l-2 border-neutral-700 pl-4">
        <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Description</h4>
        {editingDescription ? (
          <textarea
            autoFocus
            value={descriptionValue}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setDescriptionValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.target.blur(); }
              else if (e.key === "Escape") { cancelDescRef.current = true; setDescriptionValue(task.description || ""); e.target.blur(); }
            }}
            onBlur={commitDescription}
            className="w-full text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 bg-white dark:bg-gray-900 whitespace-pre-wrap resize-y min-h-[80px] focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        ) : cleanDesc ? (
          <>
            <div
              className="group cursor-pointer rounded px-2 py-1 -mx-2 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors flex items-start justify-between gap-2"
              onClick={() => { setDescriptionValue(task.description || ""); setEditingDescription(true); }}
            >
              <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap flex-1">
                {isLongDesc && !descriptionExpanded
                  ? renderDescriptionSegments(parseDescriptionSegments(`${descPreview}…`))
                  : renderDescriptionSegments(parseDescriptionSegments(cleanDesc))}
              </p>
              <svg className="w-3 h-3 mt-0.5 text-neutral-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
            </div>
            {isLongDesc && (
              <button
                type="button"
                onClick={() => setDescriptionExpanded((v) => !v)}
                className="text-xs text-blue-500 hover:text-blue-600 cursor-pointer mt-1"
              >
                {descriptionExpanded ? "See less" : "See more"}
              </button>
            )}
          </>
        ) : (
          <div
            className="group cursor-pointer rounded px-2 py-1 -mx-2 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
            onClick={() => { setDescriptionValue(task.description || ""); setEditingDescription(true); }}
          >
            <p className="text-sm text-gray-400 dark:text-gray-500 italic">No description yet</p>
          </div>
        )}
      </div>

      {/* ─── Blocked reason (factual — kept distinct from the situation) ─ */}
      {task.blocked_reason && (
        <div className="mb-4 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-0.5">Blocked</p>
          <p className="text-xs text-amber-800 dark:text-amber-300">{task.blocked_reason}</p>
        </div>
      )}

      {/* ─── Situation (always shown; placeholder when empty) */}
      <div className="mb-4 border-l-2 border-neutral-700 pl-4">
        <div className={cleanDesc ? "border-t border-gray-200 dark:border-gray-700 pt-3" : ""}>
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Situation<span className="ml-1.5 inline-flex items-center text-[9px] uppercase font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">AI</span></h4>
          {cleanedSummary || (task.stalled && task.stalled_reason) ? (
            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
              {cleanedSummary && <p>{cleanedSummary}</p>}
              {task.stalled && task.stalled_reason && (
                <>
                  <p className="italic text-amber-800 dark:text-amber-300">
                    {isLongStalled && !stalledExpanded ? `${stalledPreview}…` : stalledReason}
                  </p>
                  {isLongStalled && (
                    <button
                      type="button"
                      onClick={() => setStalledExpanded((v) => !v)}
                      className="text-xs text-blue-500 hover:text-blue-600 cursor-pointer"
                    >
                      {stalledExpanded ? "See less" : "See more"}
                    </button>
                  )}
                </>
              )}
            </div>
          ) : (
            <p className="text-sm italic text-gray-400 dark:text-gray-500">No situation data yet</p>
          )}
        </div>
      </div>

      {/* ─── Forecast (left-border panel — matches Description/Situation) */}
      <div className="mb-4 border-l-2 border-neutral-700 pl-4">
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Forecast<span className="ml-1.5 inline-flex items-center text-[9px] uppercase font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">AI</span></h4>
          {forecast && forecast.median_days != null && forecast.p90_days != null && forecast.observations ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Usually takes about {forecast.median_days} days. Almost all finish within {forecast.p90_days} days. — Based on {fmtNum(forecast.observations)} past task{forecast.observations !== 1 ? "s" : ""}.
            </p>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500 italic">
              {forecast && forecast.median_days != null && forecast.p90_days != null ? "Not enough data yet" : "Not enough data yet for a forecast"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskHeader;
